import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { tokenStore } from "@/lib/tokenStore";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import {
  Download, FileSearch, CheckCircle2, XCircle, ShieldCheck, FileText, ExternalLink,
  Loader2, Send, BadgeCheck, AlertTriangle, Eye, Inbox, Mail, UserCog, Trash2
} from "lucide-react";
import { STATUSES } from "@/lib/statuses";
import InvitationsPanel from "@/pages/admin/InvitationsPanel";
import AccountPanel from "@/pages/admin/AccountPanel";

const DOC_KEYS = [
  { key: "assurance_rc",         label: "Assurance scolaire / RC", required: true },
  { key: "certificat_naissance", label: "Certificat de naissance", required: true },
  { key: "certificat_radiation", label: "Certificat de radiation", required: false },
  { key: "bulletins",            label: "Bulletins scolaires (x3)", required: true },
  { key: "id_pere",              label: "Pièce d'identité — Père", required: true },
  { key: "id_mere",              label: "Pièce d'identité — Mère", required: true },
  { key: "id_enfant",            label: "Pièce d'identité — Enfant", required: true },
  { key: "vaccinations",         label: "Carnet de vaccination", required: true },
  { key: "iban",                 label: "IBAN", required: true },
  { key: "preuve_virement",      label: "Preuve de virement", required: true },
  { key: "dossier_signe",        label: "Dossier complet signé", required: true },
];

function StatCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="card p-5">
      <div className="overline">{label}</div>
      <div className="font-display text-4xl font-bold mt-2" style={{ color: accent || "#0F172A" }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUSES[status];
  if (!meta) return null;
  return (
    <span className="status-pill" style={{ color: meta.color, borderColor: meta.color }}>
      <span className="dot" style={{ background: meta.color }}/>
      {meta.label}
    </span>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("dossiers");
  const [stats, setStats] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState({ adminNotes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/dossiers", { params: filter ? { status: filter } : {} }),
      ]);
      setStats(s.data);
      setDossiers(list.data);
    } catch (e) {
      toast.error("Échec du chargement");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const validateDoc = async (id, key, status, note = "") => {
    try {
      const { data } = await api.post(`/admin/dossiers/${id}/documents/${key}/validate`, { status, admin_note: note });
      setSelected(data);
      setDossiers(ds => ds.map(d => d.id === id ? data : d));
      toast.success(`Pièce ${status === "validated" ? "validée" : "rejetée"}`);
    } catch (e) {
      toast.error(formatApiError(e, "Erreur"));
    }
  };

  const decide = async (id, dec) => {
    try {
      const { data } = await api.post(`/admin/dossiers/${id}/decision`, { decision: dec, admin_notes: decision.adminNotes });
      setSelected(data);
      setDossiers(ds => ds.map(d => d.id === id ? data : d));
      toast.success(dec === "validated" ? "Dossier validé" : "Dossier renvoyé pour correction");
      setDecision({ adminNotes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erreur"));
    }
  };

  const deleteDossier = async (id) => {
    const studentName = `${selected?.student?.firstname || ""} ${selected?.student?.lastname || ""}`.trim() || "ce dossier";
    const ok = window.confirm(
      `Supprimer définitivement le dossier de ${studentName} ?\n\n` +
      `Cette action supprime aussi le compte parent, les invitations associées et tous les documents téléversés. Elle est irréversible.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/dossiers/${id}`);
      toast.success("Dossier supprimé");
      setSelected(null);
      setDossiers(ds => ds.filter(d => d.id !== id));
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Suppression impossible"));
    }
  };

  const downloadExport = async () => {
    const token = tokenStore.get();
    const resp = await fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` }});
    if (!resp.ok) { toast.error("Export indisponible"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mylafayette-admissions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewFile = async (path) => {
    const token = tokenStore.get();
    const resp = await fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }});
    if (!resp.ok) { toast.error("Fichier introuvable"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const filtered = dossiers.filter(d => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      d.parent_email?.toLowerCase().includes(q) ||
      d.student?.firstname?.toLowerCase().includes(q) ||
      d.student?.lastname?.toLowerCase().includes(q) ||
      d.id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Tableau de bord administrateur</span>
          <h1 className="font-display text-4xl font-bold mt-1 flex items-center gap-2">
            <ShieldCheck size={28}/> Salle de contrôle
          </h1>
          <p className="text-sm text-slate-600 mt-1">Vue d'ensemble des inscriptions, validations, paiements et invitations.</p>
        </div>
        {tab === "dossiers" && (
          <button data-testid="export-csv-btn" onClick={downloadExport} className="btn-primary">
            <Download size={14}/> Exporter en CSV
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-2 border-b border-slate-200">
        <button
          data-testid="tab-dossiers"
          onClick={() => setTab("dossiers")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${tab === "dossiers" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <Inbox size={14}/> Dossiers d'inscription
        </button>
        <button
          data-testid="tab-invitations"
          onClick={() => setTab("invitations")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${tab === "invitations" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <Mail size={14}/> Invitations
        </button>
        <button
          data-testid="tab-account"
          onClick={() => setTab("account")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${tab === "account" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <UserCog size={14}/> Mon compte
        </button>
      </div>

      {(() => {
        if (tab === "invitations") return <div className="mt-6"><InvitationsPanel/></div>;
        if (tab === "account") return <AccountPanel/>;
        return null;
      })()}
      {tab === "dossiers" && (
      <>
      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        <StatCard testid="stat-total"     label="Total dossiers" value={stats?.total ?? 0} />
        <StatCard testid="stat-submitted" label="À examiner"     value={stats?.submitted ?? 0} accent={STATUSES.submitted.color} />
        <StatCard testid="stat-validated" label="Inscriptions validées" value={stats?.validated ?? 0} accent={STATUSES.validated.color} />
        <StatCard testid="stat-rejected"  label="À corriger"     value={stats?.rejected ?? 0} accent={STATUSES.rejected.color} />
      </div>

      {/* Filters */}
      <div className="card p-4 mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <FileSearch size={16} className="text-slate-400"/>
          <input
            data-testid="search-input"
            placeholder="Rechercher par nom, email, ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="field-input border-0 focus:ring-0 p-0"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "", l: "Tous" },
            { v: "draft", l: "Brouillon" },
            { v: "submitted", l: "À examiner" },
            { v: "validated", l: "Validés" },
            { v: "rejected", l: "Renvoyés" },
          ].map(f => (
            <button
              key={f.v || "all"}
              data-testid={`filter-${f.v || "all"}`}
              onClick={() => setFilter(f.v)}
              className={`text-xs px-3 py-1.5 rounded-full border ${filter === f.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
            >{f.l}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card mt-4 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="text-left p-3">Élève</th>
              <th className="text-left p-3">Parent</th>
              <th className="text-left p-3">Niveau</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Mis à jour</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2"/> Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Aucun dossier.</td></tr>
            )}
            {filtered.map(d => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${d.id}`}>
                <td className="p-3">
                  <div className="font-medium">{d.student?.firstname || "—"} {d.student?.lastname || ""}</div>
                  <div className="text-xs text-slate-400">{d.id}</div>
                </td>
                <td className="p-3 text-slate-700">{d.parent_email}</td>
                <td className="p-3">{d.student?.level_requested || "—"}</td>
                <td className="p-3"><StatusPill status={d.status}/></td>
                <td className="p-3 text-slate-500 text-xs">{new Date(d.updated_at).toLocaleString("fr-FR")}</td>
                <td className="p-3 text-right">
                  <button data-testid={`open-${d.id}`} onClick={() => setSelected(d)} className="btn-ghost text-xs">
                    <Eye size={12}/> Examiner
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelected(null)}/>
          <aside className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" data-testid="dossier-drawer">
            <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <span className="overline">Dossier {selected.id}</span>
                <h2 className="font-display text-2xl font-bold mt-1">
                  {selected.student?.firstname || "—"} {selected.student?.lastname || ""}
                </h2>
                <p className="text-sm text-slate-500">{selected.parent_email}</p>
                <div className="mt-3"><StatusPill status={selected.status}/></div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><XCircle size={20}/></button>
            </div>

            <div className="p-6 space-y-6">
              <section>
                <h3 className="font-display font-semibold text-lg">Élève</h3>
                <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                  <Field label="Date de naissance" value={selected.student?.date_of_birth}/>
                  <Field label="Genre" value={selected.student?.gender}/>
                  <Field label="Nationalité" value={selected.student?.nationality}/>
                  <Field label="École actuelle" value={selected.student?.current_school}/>
                  <Field label="Niveau actuel" value={selected.student?.current_level}/>
                  <Field label="Niveau demandé" value={selected.student?.level_requested}/>
                  <Field label="Options" value={(selected.student?.options || []).join(", ") || "—"} className="col-span-2"/>
                </div>
              </section>

              <section>
                <h3 className="font-display font-semibold text-lg">Parents</h3>
                <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                  <Field label="Père" value={`${selected.parents?.father_name || ""} · ${selected.parents?.father_phone || ""}`}/>
                  <Field label="Mère" value={`${selected.parents?.mother_name || ""} · ${selected.parents?.mother_phone || ""}`}/>
                  <Field label="Adresse" value={`${selected.parents?.address || ""}, ${selected.parents?.postal_code || ""} ${selected.parents?.city || ""}`} className="col-span-2"/>
                </div>
              </section>

              <section>
                <h3 className="font-display font-semibold text-lg">Pièces justificatives</h3>
                <div className="mt-3 space-y-2">
                  {DOC_KEYS.map(d => {
                    const rec = selected.documents?.[d.key];
                    return (
                      <div key={d.key} className="card p-3 flex items-center gap-3" data-testid={`admin-doc-${d.key}`}>
                        <div className="w-9 h-9 rounded-md bg-slate-900 text-white grid place-items-center"><FileText size={14}/></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            {d.label}
                            {!d.required && <span className="text-[9px] tracking-[0.18em] uppercase font-semibold text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50">Facultatif</span>}
                          </div>
                          {rec ? (
                            <div className="text-xs text-slate-500 truncate">{rec.original_filename}</div>
                          ) : <div className={`text-xs ${d.required ? "text-red-500" : "text-slate-400"}`}>{d.required ? "Non fourni" : "Non fourni (facultatif)"}</div>}
                          {rec && (
                            <div className="mt-1">
                              <StatusPill status={rec.status}/>
                            </div>
                          )}
                        </div>
                        {rec && (
                          <div className="flex items-center gap-2">
                            <button data-testid={`preview-${d.key}`} onClick={() => previewFile(rec.storage_path)} className="btn-ghost text-xs">
                              <ExternalLink size={12}/> Voir
                            </button>
                            <button data-testid={`validate-doc-${d.key}`} onClick={() => validateDoc(selected.id, d.key, "validated")} className="btn-ghost text-xs">
                              <CheckCircle2 size={12}/> Valider
                            </button>
                            <button data-testid={`reject-doc-${d.key}`} onClick={() => {
                              const note = window.prompt("Note pour les parents (optionnel) :", rec.admin_note || "");
                              if (note !== null) validateDoc(selected.id, d.key, "rejected", note);
                            }} className="btn-ghost text-xs">
                              <XCircle size={12}/> Rejeter
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {selected.status === "submitted" && (
                <section className="card p-4 bg-slate-50">
                  <h3 className="font-display font-semibold text-lg">Décision finale</h3>
                  <p className="text-xs text-slate-500 mt-1">Validez toutes les pièces avant d'approuver le dossier.</p>
                  <textarea
                    data-testid="admin-notes"
                    placeholder="Note adressée aux parents (en cas de rejet, expliquez ce qui doit être corrigé)…"
                    value={decision.adminNotes}
                    onChange={(e) => setDecision({ adminNotes: e.target.value })}
                    className="field-input mt-3 min-h-[80px]"
                  />
                  <div className="flex gap-3 mt-3">
                    <button data-testid="decision-validate" onClick={() => decide(selected.id, "validated")} className="btn-primary">
                      <BadgeCheck size={14}/> Valider le dossier
                    </button>
                    <button data-testid="decision-reject" onClick={() => decide(selected.id, "rejected")} className="btn-ghost">
                      <Send size={14}/> Renvoyer pour correction
                    </button>
                  </div>
                </section>
              )}

              {selected.status === "validated" && (
                <div className="card p-4 border-emerald-200 bg-emerald-50 text-sm text-emerald-800 flex flex-col gap-3">
                  <div className="flex gap-2">
                    <BadgeCheck size={16} className="shrink-0 mt-0.5"/>
                    <span>Inscription confirmée. Le parent sera notifié des étapes de rentrée.</span>
                  </div>
                  <button
                    data-testid="admin-download-certificate"
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/admin/dossiers/${selected.id}/certificate`, { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url;
                        const last = (selected?.student?.lastname || "eleve").toUpperCase().replace(/\s+/g, "_");
                        const first = (selected?.student?.firstname || "").replace(/\s+/g, "_");
                        a.download = first ? `Certificat_inscription_${last}_${first}.pdf` : `Certificat_inscription_${last}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (e) {
                        toast.error(formatApiError(e, "Téléchargement impossible"));
                      }
                    }}
                    className="btn-cta text-xs self-start"
                  >
                    <Download size={12}/> Certificat d'inscription
                  </button>
                </div>
              )}
              {selected.status === "rejected" && (
                <div className="card p-4 border-red-200 bg-red-50 text-sm text-red-700 flex gap-2">
                  <AlertTriangle size={16}/> Renvoyé pour correction · {selected.admin_notes || "Pas de note"}
                </div>
              )}

              <section className="card p-4 border-red-200 bg-red-50/40">
                <h3 className="font-display font-semibold text-lg text-red-700 flex items-center gap-2">
                  <Trash2 size={16}/> Zone de danger
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  La suppression efface le dossier, le compte parent associé, les invitations et tous les documents téléversés. Cette action est <strong>irréversible</strong>.
                </p>
                <button
                  data-testid="delete-dossier-btn"
                  type="button"
                  onClick={() => deleteDossier(selected.id)}
                  className="btn-ghost text-xs mt-3 text-red-700 border-red-300 hover:bg-red-100"
                >
                  <Trash2 size={12}/> Supprimer définitivement
                </button>
              </section>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="overline">{label}</div>
      <div className="mt-1 text-slate-800">{value || "—"}</div>
    </div>
  );
}
